import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { AlertController, ModalController, ToastController } from "@ionic/angular";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LogReporterService } from "src/app/_services/log-reporter.service";
import { scrubLogText } from "src/app/_services/log-scrubber";
import { LogEntry, LogLevel, LogService } from "src/app/_services/log.service";

type LevelFilter = "all" | LogLevel;

@Component({
  selector: "app-log-viewer-modal",
  templateUrl: "./log-viewer-modal.component.html",
  styleUrls: ["./log-viewer-modal.component.scss"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogViewerModalComponent implements OnInit, OnDestroy {
  allEntries: LogEntry[] = [];
  filteredEntries: LogEntry[] = [];
  levelFilter: LevelFilter = "all";
  search = "";
  /**
   * Scrubbed view is the SAFE default — masks bearer tokens, JWTs, emails,
   * phone numbers, GPS, etc. before rendering / copying. Disable at your
   * own risk (e.g. when debugging the scrubber itself).
   */
  showRaw = false;
  private destroy$ = new Subject<void>();

  sending = false;

  constructor(
    private logService: LogService,
    private logReporter: LogReporterService,
    private modalController: ModalController,
    private toastController: ToastController,
    private alertController: AlertController,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.logService.entries$
      .pipe(takeUntil(this.destroy$))
      .subscribe((entries) => {
        this.allEntries = entries;
        this.applyFilters();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setLevelFilter(level: LevelFilter): void {
    this.levelFilter = level;
    this.applyFilters();
  }

  onSearchChange(value: any): void {
    this.search = (value?.detail?.value ?? value ?? "").toString();
    this.applyFilters();
  }

  toggleShowRaw(): void {
    this.showRaw = !this.showRaw;
    this.cdr.markForCheck();
  }

  /** Message text to display / copy. Scrubbed unless `showRaw` is explicitly on. */
  displayMessage(entry: LogEntry): string {
    return this.showRaw ? entry.message : scrubLogText(entry.message);
  }

  trackById(_i: number, e: LogEntry): number {
    return e.id;
  }

  levelClass(level: LogLevel): string {
    return `log-entry--${level}`;
  }

  formatTime(ts: number): string {
    const d = new Date(ts);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, "0")}`;
  }

  async copyAll(): Promise<void> {
    const lines = this.allEntries.map((e) => this.formatForCopy(e));
    const ok = await this.writeToClipboard(lines.join("\n"));
    await this.presentToast(
      ok
        ? this.showRaw
          ? "Logs copiés (brut — contient des PII !)"
          : "Logs copiés (anonymisés)"
        : "Impossible de copier",
      ok ? (this.showRaw ? "warning" : "success") : "danger"
    );
  }

  async copyVisible(): Promise<void> {
    const lines = this.filteredEntries.map((e) => this.formatForCopy(e));
    const ok = await this.writeToClipboard(lines.join("\n"));
    await this.presentToast(
      ok
        ? this.showRaw
          ? "Vue filtrée copiée (brut)"
          : "Vue filtrée copiée (anonymisée)"
        : "Impossible de copier",
      ok ? (this.showRaw ? "warning" : "success") : "danger"
    );
  }

  /** Single source of truth for copy / share formatting; honors showRaw. */
  private formatForCopy(e: LogEntry): string {
    const ts = new Date(e.timestamp).toISOString();
    const msg = this.showRaw ? e.message : scrubLogText(e.message);
    return `[${ts}] [${e.level.toUpperCase()}] ${msg}`;
  }

  async clear(): Promise<void> {
    this.logService.clear();
    await this.presentToast("Logs effacés", "medium");
  }

  /**
   * Explicit-consent flow before any upload (Apple 5.1 / Google Play User
   * Data). The dialog discloses, in plain French:
   *   - WHAT data leaves the device (categories + masked fields)
   *   - WHY (support debugging + AI triage)
   *   - WHERE it goes (our backend + Anthropic Claude for analysis)
   *   - HOW LONG it's kept (60 days, then auto-deleted)
   * The user must tap "Envoyer" each time — no implicit / persistent consent.
   */
  async sendToSupport(): Promise<void> {
    if (this.sending) return;
    const alert = await this.alertController.create({
      header: "Envoyer le log au support",
      subHeader: "Données collectées",
      message:
        "<p><b>Ce qui sera envoyé :</b><br>" +
        "• Les lignes de log JS récentes (anonymisées)<br>" +
        "• Version de l'app, plateforme, route, votre identifiant utilisateur<br>" +
        "• Adresse IP hachée (non réversible)</p>" +
        "<p><b>Ce qui est masqué avant envoi :</b><br>" +
        "tokens d'authentification, emails, numéros de téléphone, " +
        "coordonnées GPS précises (arrondies à ~1&nbsp;km).</p>" +
        "<p><b>Finalité :</b> diagnostic de bug. Le contenu sera analysé " +
        "par notre service backend et par un modèle d'IA (Anthropic Claude) " +
        "pour aider à identifier le problème. Conservé 60 jours puis supprimé.</p>" +
        "<p><b>Vos droits :</b> vous pouvez demander la suppression " +
        "(droit à l'effacement RGPD) en contactant le support.</p>",
      inputs: [
        {
          name: "note",
          type: "textarea",
          placeholder: "Que faisiez-vous quand le bug est arrivé ? (optionnel)",
          attributes: { maxlength: 500 },
        },
        {
          name: "consent",
          type: "checkbox",
          label: "J'accepte l'envoi de ces données pour analyse.",
          value: "yes",
          checked: false,
        },
      ],
      buttons: [
        { text: "Annuler", role: "cancel" },
        {
          text: "Envoyer",
          handler: async (values: { note?: string; consent?: string[] }) => {
            // Hard gate: consent checkbox must be ticked, every send.
            const consented = Array.isArray(values?.consent)
              ? values.consent.includes("yes")
              : !!values?.consent;
            if (!consented) {
              await this.presentToast(
                "Cochez la case de consentement pour envoyer",
                "warning"
              );
              return false;
            }
            this.sending = true;
            this.cdr.markForCheck();
            const result = await this.logReporter.sendManual(values?.note);
            this.sending = false;
            this.cdr.markForCheck();
            const messages: Record<string, string> = {
              sent: "Log envoyé au support",
              empty: "Aucun log à envoyer",
              network: "Échec de l'envoi (pas de réseau ?)",
              unauthenticated: "Connectez-vous pour envoyer le log",
              "rate-limited": "Trop d'envois récents — réessayez dans quelques minutes",
              "server-error": "Erreur côté serveur — l'équipe a été notifiée",
              "client-error": "Format invalide — contactez le support",
            };
            await this.presentToast(
              messages[result.reason] || (result.ok ? "Envoyé" : "Échec"),
              result.ok ? "success" : "danger"
            );
            return true;
          },
        },
      ],
    });
    await alert.present();
  }

  close(): void {
    this.modalController.dismiss();
  }

  private applyFilters(): void {
    const q = this.search.trim().toLowerCase();
    this.filteredEntries = this.allEntries.filter((e) => {
      if (this.levelFilter !== "all" && e.level !== this.levelFilter) return false;
      if (q && !e.message.toLowerCase().includes(q)) return false;
      return true;
    });
    this.cdr.markForCheck();
  }

  /**
   * Best-effort clipboard write. Tries the async Clipboard API first (works
   * on iOS WKWebView 13.4+ and Android WebView 66+), falls back to a hidden
   * textarea + document.execCommand for older shells.
   */
  private async writeToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) {
      // fall through to legacy path
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch (_) {
      return false;
    }
  }

  private async presentToast(message: string, color: string): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 1600,
      position: "top",
      color,
    });
    await toast.present();
  }
}
